/*
 * Ticking any set of groups: a filter over two lists of check boxes, groups and people, and a line
 * that counts what is ticked.
 *
 * It is the body of *Choose groups* (`GroupPicker.tsx`'s `ChooseGroupsDialog`) taken out of the
 * dialog, so that a form which asks the same question inline — New channel's *Chosen groups* — draws
 * the very same lists instead of opening a dialog over its own. The two lists are kept apart because
 * a person looks for "the designers" and for "Anna" in different places (`groupOptions.ts`); a
 * caller whose candidates hold no personal group simply gets one list.
 *
 * The checklist is controlled: the caller keeps the ticked ids and decides what an empty set means.
 * Under the count stands one polite line: *Choose at least one* while nothing is ticked and, where the
 * caller asks for it (`warnWhenNotIn`), that the reader is in none of the ticked groups — a warning
 * that matters for a thing the reader would lose sight of, and is noise where the reader stays in
 * whatever they make.
 */

import { useId, useMemo, useState } from "react"
import { Field, ListState } from "../../ui/index.ts"
import { t } from "../i18n/index.ts"
import { chipPerson, PersonFace, PersonName, type ChipPerson } from "../people/PersonChip.tsx"
import { useModel } from "../shell/index.ts"
import type { GroupId, GroupView } from "../store/model.ts"
import { chipGroup, isPersonalGroup } from "./group.ts"
import { groupOptions, matchesOption, type GroupOption, type GroupOptions } from "./groupOptions.ts"
import "./groups.css"

export interface GroupChecklistProps {
	/** Every group the reader may tick; the caller's policy decides. */
	candidates: readonly GroupView[]
	/** The ticked groups' ids. */
	value: readonly GroupId[]
	onChange: (next: GroupId[]) => void
	/** What the ticks are for, as the set's caption: "Groups that can find it". */
	legend: string
	/** Draws the caption for a screen reader only, where a dialog's title already says it. */
	legendHidden?: boolean
	/** Whether to say so when the reader is in none of the ticked groups. Default false. */
	warnWhenNotIn?: boolean
}

/** The picker's rows for these candidates, as the reader's model names them. */
export function useGroupOptions(candidates: readonly GroupView[]): GroupOptions {
	const users = useModel((model) => model.users)
	const reader = useModel((model) => model.me?.id ?? 0)
	return useMemo(() => groupOptions(candidates, users, reader), [candidates, users, reader])
}

/** What a row says under its name: a group's handle and size, a person's handle, or "Nobody else". */
function detailOf(option: GroupOption): string | undefined {
	const { group, person } = option
	if (!isPersonalGroup(group)) {
		return t("groups.card.meta", { handle: group.handle, count: group.memberIds.length })
	}
	if (option.holdsReader) return t("groups.choose.me.hint")
	return person === undefined ? undefined : `@${person.handle}`
}

/**
 * One option drawn the way the person family draws anybody — the face, the name, a quiet line — and
 * put together from the family's two parts rather than as a `PersonChip`, because the face and the
 * name are not always the same account's: "Only me" wears the reader's own picture and initials,
 * where a chip would have drawn an "O" for the words.
 */
export function OptionChip({
	option,
	detail,
	detailId,
}: {
	option: GroupOption
	detail?: string | undefined
	/** The id of the detail, for a control that it describes. */
	detailId?: string | undefined
}) {
	const face: ChipPerson =
		option.person === undefined
			? isPersonalGroup(option.group)
				? { id: 0, name: option.label }
				: chipGroup(option.group)
			: chipPerson(option.person)
	return (
		<span className="tb-person-chip">
			<PersonFace person={face} hover={false} />
			<span className="tb-person-chip__text">
				<PersonName person={{ ...face, name: option.label }} hover={false} />
				{detail === undefined ? null : (
					<span id={detailId} className="tb-person-chip__detail">
						{detail}
					</span>
				)}
			</span>
		</span>
	)
}

export function GroupChecklist({
	candidates,
	value,
	onChange,
	legend,
	legendHidden = false,
	warnWhenNotIn = false,
}: GroupChecklistProps) {
	const options = useGroupOptions(candidates)
	const legendId = useId()
	const [query, setQuery] = useState("")

	const groups = options.groups.filter((option) => matchesOption(option, query))
	const people = options.people.filter((option) => matchesOption(option, query))
	const holdsReader = value.some((id) => options.byId.get(id)?.holdsReader === true)
	// What the reader should know before the set is used; said politely, as it changes.
	const warning =
		value.length === 0
			? t("groups.choose.atLeastOne")
			: warnWhenNotIn && !holdsReader
				? t("groups.choose.notIn")
				: ""

	function toggle(id: GroupId, on: boolean) {
		onChange(on ? [...value, id] : value.filter((other) => other !== id))
	}

	// A labelled group rather than a fieldset: the lists inside are groups of their own, and a
	// fieldset would bring the browser's frame into a dialog that already frames them.
	return (
		<div className="tb-form" role="group" aria-labelledby={legendId}>
			<p id={legendId} className={legendHidden ? "tb-visually-hidden" : "tb-choices__legend"}>
				{legend}
			</p>
			{/* The filter is named after what it can find: a channel's owners are never a person. */}
			<Field
				label={
					options.people.length === 0 ? t("groups.filter.label") : t("groups.choose.filter.label")
				}
			>
				{(control) => (
					<input
						{...control}
						type="search"
						value={query}
						placeholder={t("groups.choose.filter.placeholder")}
						onChange={(event) => setQuery(event.target.value)}
						// The lists follow as one types, so Enter has nothing to do here — and inside a
						// form (New channel) it would otherwise send the form half filled.
						onKeyDown={(event) => {
							if (event.key === "Enter") event.preventDefault()
						}}
					/>
				)}
			</Field>
			{groups.length === 0 && people.length === 0 ? (
				<ListState.Empty title={t("groups.choose.noMatch")} />
			) : (
				<div className="tb-group-picker__well">
					<OptionList
						title={t("groups.choose.groups")}
						options={groups}
						ticked={value}
						onToggle={toggle}
					/>
					<OptionList
						title={t("groups.choose.people")}
						options={people}
						ticked={value}
						onToggle={toggle}
					/>
				</div>
			)}
			<div>
				<p className="tb-person__meta">{t("groups.picker.selected", { count: value.length })}</p>
				<p className="tb-field__hint" aria-live="polite">
					{warning}
				</p>
			</div>
		</div>
	)
}

/** One of the two lists, under its caption; a list the filter emptied is not drawn. */
function OptionList({
	title,
	options,
	ticked,
	onToggle,
}: {
	title: string
	options: GroupOption[]
	ticked: readonly GroupId[]
	onToggle: (id: GroupId, on: boolean) => void
}) {
	const titleId = useId()
	if (options.length === 0) return null
	return (
		<div role="group" aria-labelledby={titleId}>
			<p className="tb-group-picker__caption" id={titleId}>
				{title}
			</p>
			<ul className="tb-group-picker__list">
				{options.map((option) => (
					<li key={option.group.id}>
						<label className="tb-picker__option">
							<input
								type="checkbox"
								checked={ticked.includes(option.group.id)}
								onChange={(event) => onToggle(option.group.id, event.target.checked)}
							/>
							<OptionChip option={option} detail={detailOf(option)} />
						</label>
					</li>
				))}
			</ul>
		</div>
	)
}
